// Browser & Audio Notification Utilities for Background Tasks

let audioCtx = null;

/**
 * Initializes or resumes Web Audio context. Must be invoked during or after a user gesture.
 */
export function initAudioContext() {
  try {
    if (!audioCtx && (typeof window !== "undefined") && (window.AudioContext || window.webkitAudioContext)) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      audioCtx = new AudioCtx();
    }
    if (audioCtx && audioCtx.state === "suspended") {
      audioCtx.resume().catch(() => {});
    }
  } catch (err) {
    console.warn("AudioContext init notice:", err);
  }
  return audioCtx;
}

/**
 * Checks if the Web Notification API is supported in the current environment.
 */
export function isNotificationSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

/**
 * Returns current permission status: 'granted', 'denied', 'default', or 'unsupported'.
 */
export function getNotificationPermission() {
  if (!isNotificationSupported()) return "unsupported";
  return Notification.permission;
}

/**
 * Requests permission for Desktop Notifications.
 * Call this during a user gesture (e.g. clicking 'Generate Recipe' or 'Enable Notifications').
 */
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) return "unsupported";
  if (Notification.permission === "default") {
    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (err) {
      console.warn("Notification.requestPermission error:", err);
      return Notification.permission;
    }
  }
  return Notification.permission;
}

/**
 * Synthesizes a crisp, gentle chime using the Web Audio API.
 * Completely self-contained: requires 0 external audio files, zero latency, works offline and in background tabs.
 */
export function playSuccessChime() {
  try {
    const ctx = initAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    // Pleasant ascending arpeggio: C5 (523Hz) -> E5 (659Hz) -> G5 (784Hz) -> C6 (1046Hz)
    const notes = [
      { freq: 523.25, start: 0, duration: 0.12 },
      { freq: 659.25, start: 0.09, duration: 0.12 },
      { freq: 783.99, start: 0.18, duration: 0.18 },
      { freq: 1046.50, start: 0.28, duration: 0.40 }
    ];

    for (const note of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(note.freq, now + note.start);

      gain.gain.setValueAtTime(0.0001, now + note.start);
      gain.gain.exponentialRampToValueAtTime(0.16, now + note.start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + note.start + note.duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + note.start);
      osc.stop(now + note.start + note.duration + 0.05);
    }
  } catch (err) {
    console.warn("Audio chime could not be played:", err);
  }
}

/**
 * Synthesizes a subtle descending notification sound for failure/error alerts.
 */
export function playErrorChime() {
  try {
    const ctx = initAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const notes = [
      { freq: 440.00, start: 0, duration: 0.15 },
      { freq: 349.23, start: 0.14, duration: 0.30 }
    ];

    for (const note of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(note.freq, now + note.start);

      gain.gain.setValueAtTime(0.0001, now + note.start);
      gain.gain.exponentialRampToValueAtTime(0.12, now + note.start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + note.start + note.duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + note.start);
      osc.stop(now + note.start + note.duration + 0.05);
    }
  } catch (err) {
    console.warn("Error audio chime could not be played:", err);
  }
}

let titleInterval = null;
let originalDocumentTitle = "";

/**
 * Cycles the browser tab title between an alert message and the original title when tab is in the background.
 */
export function flashTabTitle(alertText, maxSeconds = 45) {
  if (typeof document === "undefined" || !document.hidden) return;

  if (!originalDocumentTitle) {
    originalDocumentTitle = document.title.replace(/^✨\s+/, "");
  }

  if (titleInterval) clearInterval(titleInterval);

  let showAlt = true;
  document.title = `✨ ${alertText}`;

  titleInterval = setInterval(() => {
    if (!document.hidden) {
      stopFlashingTabTitle();
      return;
    }
    showAlt = !showAlt;
    document.title = showAlt ? `✨ ${alertText}` : originalDocumentTitle;
  }, 1200);

  const cleanup = () => {
    stopFlashingTabTitle();
    window.removeEventListener("focus", cleanup);
    document.removeEventListener("visibilitychange", cleanup);
  };

  window.addEventListener("focus", cleanup, { once: true });
  document.addEventListener("visibilitychange", cleanup, { once: true });

  setTimeout(() => {
    stopFlashingTabTitle();
  }, maxSeconds * 1000);
}

/**
 * Stops tab title flashing and restores the original title.
 */
export function stopFlashingTabTitle() {
  if (titleInterval) {
    clearInterval(titleInterval);
    titleInterval = null;
  }
  if (originalDocumentTitle && typeof document !== "undefined") {
    document.title = originalDocumentTitle;
    originalDocumentTitle = "";
  }
}

/**
 * Triggers a desktop notification, audio chime, and tab title flashing when recipe generation completes.
 */
export function notifyRecipeReady(recipeTitle) {
  const displayTitle = recipeTitle ? `"${recipeTitle}"` : "New Recipe";

  // 1. Play synthesized audio chime (works in background tabs)
  playSuccessChime();

  // 2. Dispatch Desktop/Browser Notification if permission granted
  if (isNotificationSupported() && Notification.permission === "granted") {
    try {
      const notif = new Notification("Recipe Ready! ✨", {
        body: `${displayTitle} has finished generating and is ready for review.`,
        icon: "/favicon.png",
        tag: "recipe-generation-ready"
      });

      notif.onclick = () => {
        window.focus();
        notif.close();
      };
    } catch (err) {
      console.warn("Failed to create Desktop Notification:", err);
    }
  }

  // 3. Flash browser tab title if tab is inactive/hidden
  if (typeof document !== "undefined" && document.hidden) {
    flashTabTitle("Recipe Ready! 🍲");
  }
}

/**
 * Triggers notification if recipe generation encounters an error.
 */
export function notifyRecipeError(errorMessage) {
  playErrorChime();

  if (isNotificationSupported() && Notification.permission === "granted") {
    try {
      const notif = new Notification("Recipe Generation Failed ❌", {
        body: errorMessage || "The AI recipe pipeline encountered an error.",
        icon: "/favicon.png",
        tag: "recipe-generation-failed"
      });

      notif.onclick = () => {
        window.focus();
        notif.close();
      };
    } catch (err) {
      console.warn("Failed to create Desktop Notification:", err);
    }
  }

  if (typeof document !== "undefined" && document.hidden) {
    flashTabTitle("Recipe Generation Failed ❌");
  }
}

/**
 * Triggers notification when AI photo generation completes.
 */
export function notifyPhotoReady(recipeTitle) {
  playSuccessChime();

  const displayTitle = recipeTitle ? `"${recipeTitle}"` : "Recipe";
  if (isNotificationSupported() && Notification.permission === "granted") {
    try {
      const notif = new Notification("Recipe Photo Ready! 📸", {
        body: `AI photo for ${displayTitle} has finished uploading.`,
        icon: "/favicon.png",
        tag: "photo-generation-ready"
      });

      notif.onclick = () => {
        window.focus();
        notif.close();
      };
    } catch (err) {
      console.warn("Failed to create Desktop Notification:", err);
    }
  }

  if (typeof document !== "undefined" && document.hidden) {
    flashTabTitle("Photo Ready! 📸");
  }
}
